/**
 * Mängel an Bestandsanlagen (tri-server docs/defects.md): die Liste des Mandanten, gefiltert nach
 * Status, Liegenschaft, Gebäude und „mir zugewiesen", mit CSV-Export.
 *
 * Gemeldet wird in der App (Hausmeister, Techniker, Pflicht-Checkliste) oder von der
 * Dokumentanalyse; hier sichtet der Disponent, weist zu, setzt Fristen und schließt ab — im
 * Detaildialog. Anlegen kann er hier auch selbst („Mangel anlegen", ohne QR-Code an der Anlage). Der Filter steht in der Adresse, damit ein Link auf „überfällig" genau das zeigt.
 */

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { useSearchParams } from "react-router-dom";
import { Download, Plus, RefreshCw, ShieldAlert, Wrench } from "lucide-react";
import { Building, Site, User, from, is } from "@the-real-insight/tri-model";

import type { ColumnDef } from "@tanstack/react-table";

import { authenticationAtom } from "../../State";
import { userAPI } from "../../UserAPI";
import { useDefectsAPI, type Defect } from "../../DefectsAPI";
import { useClaimsAPI } from "../../ClaimsAPI";
import { useRoleAccess } from "../../hooks/useRoleAccess";
import { siteLabel } from "../../cbos/projectSites";
import { entityIdAsString } from "../../utilities/buildingLookup";
import CreateDefectDialog from "./CreateDefectDialog";
import DefectDetailDialog, { DefectStatusBadge } from "./DefectDetailDialog";
import ClaimsRadar, { RADAR_HORIZONS } from "./ClaimsRadar";
import { isClaimCase } from "./claimModel";
import {
  SEVERITY_LABEL,
  STATUS_CHOICES,
  applyFilterToSearchParams,
  defectQuery,
  defectTitle,
  formatDateTime,
  filterFromSearchParams,
  isDispatcher,
  userLabel,
  viewerUserId,
  type DefectFilter,
  type StatusChoice,
} from "./defectModel";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";

/** Radix-Select kennt keinen leeren Wert — „alle" ist deshalb ein eigener. */
const DEFAULT_RADAR_DAYS = 90;

const ALL = "__all__";

const queryRows = async (query: any): Promise<any[]> => {
  const rows = await userAPI.query(query);

  return Array.isArray(rows) ? rows : [];
};

export default function DefectsPanel() {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const customer: string | undefined = authentication?.customer;
  const roleAccess = useRoleAccess();
  const defectsAPI = useDefectsAPI(customer ?? "");
  const claimsAPI = useClaimsAPI(customer ?? "");
  const [searchParams, setSearchParams] = useSearchParams();
  const mineId = useId();

  const filter = useMemo(() => filterFromSearchParams(searchParams), [searchParams]);
  const query = useMemo(() => defectQuery(filter), [filter]);
  // Reiter und Horizont des Radars stehen wie der Filter in der Adresse.
  const view = searchParams.get("view") === "claims" ? "claims" : "defects";
  const radarDays = useMemo(() => {
    const days = Number(searchParams.get("days"));

    return (RADAR_HORIZONS as readonly number[]).includes(days) ? days : DEFAULT_RADAR_DAYS;
  }, [searchParams]);
  // Das Radar nur, wo es Bauverträge gibt — ohne (oder mit abgeschaltetem Server-Schalter) bleibt es weg.
  const [claimsAvailable, setClaimsAvailable] = useState(false);

  const [defects, setDefects] = useState<Defect[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<Defect | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  // `null`, solange die Nutzerliste lädt — dann steht „…" statt eines Namens.
  const [users, setUsers] = useState<Record<string, any> | null>(null);
  const [sites, setSites] = useState<any[]>([]);
  const [buildings, setBuildings] = useState<any[]>([]);

  const viewer = useMemo(
    () => ({ userId: viewerUserId(authentication, users), dispatcher: isDispatcher(roleAccess) }),
    [authentication, users, roleAccess],
  );
  const nameLabels = useMemo(
    () => ({ system: t("defectsPanel.system"), unknown: t("defectsPanel.unknownUser"), loading: "…", empty: "—" }),
    [t],
  );

  const setParam = useCallback(
    (key: string, value: string | undefined) =>
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value) next.set(key, value);
          else next.delete(key);

          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const openDefect = async (id: string) => {
    try {
      setSelected(await defectsAPI.getDefect(id));
    } catch (openError: any) {
      toast.error(t("defectsPanel.loadError", { error: openError?.message ?? String(openError) }));
    }
  };

  const setFilter = useCallback(
    (change: Partial<DefectFilter>) =>
      setSearchParams((current) => applyFilterToSearchParams(current, { ...filter, ...change }), { replace: true }),
    [filter, setSearchParams],
  );

  const load = useCallback(async () => {
    if (!customer) return;

    setLoading(true);
    setError(undefined);

    try {
      const result = await defectsAPI.listDefects(query);
      setDefects(result.defects);
      setTruncated(result.truncated);
    } catch (loadError: any) {
      setError(loadError?.message ?? String(loadError));
      setDefects([]);
      setTruncated(false);
    } finally {
      setLoading(false);
    }
  }, [customer, query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!customer) return;

    claimsAPI
      .hasContracts()
      .then(setClaimsAvailable)
      .catch(() => setClaimsAvailable(false));
  }, [customer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Namen für Verantwortliche und Verlauf, und die Auswahl beim Zuweisen. Ein Fehlschlag lässt die
  // Ids stehen, die Liste bleibt benutzbar.
  useEffect(() => {
    if (!customer) return;

    queryRows(from(User.type).where(is("customer").equal(customer)))
      .then((rows) =>
        setUsers(
          rows.reduce((map: Record<string, any>, user: any) => {
            const id = entityIdAsString(user?._id);
            if (id) map[id] = user;

            return map;
          }, {}),
        ),
      )
      // Ohne Liste heißen die Personen „Unbekannt", die Mängel bleiben lesbar.
      .catch(() => setUsers({}));
    queryRows(from(Site.type).where(is("_tenant").equal(customer)))
      .then((rows) => setSites(rows.sort((a, b) => siteLabel(a).localeCompare(siteLabel(b), "de"))))
      .catch(() => setSites([]));
  }, [customer]);

  // Die Gebäude der gewählten Liegenschaft — dieselbe Kante (`Site.buildings`), über die der Server filtert.
  useEffect(() => {
    const site = sites.find((s) => entityIdAsString(s?._id) === filter.site);
    const ids = (site?.buildings ?? []).map((b: any) => entityIdAsString(b)).filter(Boolean);

    if (!ids.length) {
      setBuildings([]);
      return;
    }

    queryRows(from(Building.type).where(is("_id").in(ids) as any))
      .then(setBuildings)
      .catch(() => setBuildings([]));
  }, [sites, filter.site]);

  const exportCsv = async (format: "csv" | "materials") => {
    try {
      await defectsAPI.downloadCsv(query, format);
    } catch (exportError: any) {
      toast.error(t("defectsPanel.exportError", { error: exportError?.message ?? String(exportError) }));
    }
  };

  const columns = useMemo<ColumnDef<Defect>[]>(
    () => [
      {
        accessorKey: "mark",
        header: t("defectsPanel.columns.mark"),
        cell: ({ row }) => <span className="whitespace-nowrap font-mono text-xs">{row.original.mark ?? "—"}</span>,
      },
      {
        accessorKey: "title",
        header: t("defectsPanel.columns.title"),
        cell: ({ row }) => <span className="font-medium">{defectTitle(row.original)}</span>,
      },
      {
        id: "asset",
        header: t("defectsPanel.columns.asset"),
        cell: ({ row }) => {
          const info = row.original.assetInfo;

          return (
            <div className="flex flex-col">
              <span>{info?.name || info?.id || row.original.asset}</span>
              {info?.name && info?.id ? <span className="text-xs text-muted-foreground">{info.id}</span> : null}
            </div>
          );
        },
      },
      {
        accessorKey: "severity",
        header: t("defectsPanel.columns.severity"),
        cell: ({ row }) => (
          <Badge variant={row.original.severity === "critical" ? "destructive" : "outline"}>
            {SEVERITY_LABEL[row.original.severity] ?? row.original.severity}
          </Badge>
        ),
      },
      {
        accessorKey: "status",
        header: t("defectsPanel.columns.status"),
        cell: ({ row }) => <DefectStatusBadge status={row.original.status} />,
      },
      {
        id: "warranty",
        header: t("claims.column"),
        cell: ({ row }) =>
          isClaimCase(row.original) ? (
            <Badge variant="outline" className="whitespace-nowrap">
              <ShieldAlert className="mr-1 h-3 w-3" aria-hidden />
              {t("claims.warranty.claimCase")}
            </Badge>
          ) : null,
      },
      {
        accessorKey: "deadline",
        header: t("defectsPanel.columns.deadline"),
        cell: ({ row }) => <span className="whitespace-nowrap">{formatDateTime(row.original.deadline)}</span>,
      },
      {
        accessorKey: "overdue",
        header: t("defectsPanel.columns.overdue"),
        cell: ({ row }) =>
          row.original.overdue ? <Badge variant="destructive">{t("defectsPanel.overdue")}</Badge> : null,
      },
      {
        accessorKey: "assignee",
        header: t("defectsPanel.columns.assignee"),
        cell: ({ row }) => userLabel(users, row.original.assignee, { ...nameLabels, empty: "—" }),
      },
    ],
    [t, users, nameLabels],
  );

  if (!customer) {
    return null;
  }

  const detail = (
    <DefectDetailDialog
      defect={selected}
      customer={customer}
      users={users}
      viewer={viewer}
      onClose={() => setSelected(undefined)}
      onChanged={() => void load()}
    />
  );

  const tabs = claimsAvailable ? (
    <Tabs value={view} onValueChange={(next) => setParam("view", next === "claims" ? "claims" : undefined)}>
      <TabsList>
        <TabsTrigger value="defects">{t("claims.tabs.defects")}</TabsTrigger>
        <TabsTrigger value="claims" data-guide-id="settings.defects.claimsTab">
          {t("claims.tabs.atRisk")}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  ) : null;

  if (claimsAvailable && view === "claims") {
    return (
      <div className="flex flex-col gap-4">
        {tabs}
        <ClaimsRadar
          customer={customer}
          days={radarDays}
          onDaysChange={(days) => setParam("days", days === DEFAULT_RADAR_DAYS ? undefined : String(days))}
          onOpenDefect={(id) => void openDefect(id)}
        />
        {detail}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {tabs}
      <div className="flex items-start justify-between gap-4">
        <p className="max-w-3xl text-sm text-muted-foreground">{t("defectsPanel.description")}</p>
        <div className="flex shrink-0 gap-2">
          {viewer.dispatcher ? (
            <Button size="sm" data-guide-id="settings.defects.create" onClick={() => setCreating(true)}>
              <Plus className="mr-2 h-4 w-4" aria-hidden />
              {t("defectsPanel.create.open")}
            </Button>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => void exportCsv("csv")}>
            <Download className="mr-2 h-4 w-4" aria-hidden />
            {t("defectsPanel.exportDefects")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => void exportCsv("materials")}>
            <Download className="mr-2 h-4 w-4" aria-hidden />
            {t("defectsPanel.exportMaterials")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
            {t("defectsPanel.refresh")}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <Label>{t("defectsPanel.filter.status")}</Label>
          <Select value={filter.status} onValueChange={(value) => setFilter({ status: value as StatusChoice })}>
            <SelectTrigger className="w-48" aria-label={t("defectsPanel.filter.status")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_CHOICES.map((choice) => (
                <SelectItem key={choice} value={choice}>
                  {t(`defectsPanel.statusChoice.${choice}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <Label>{t("defectsPanel.filter.site")}</Label>
          <Select
            value={filter.site ?? ALL}
            onValueChange={(value) => setFilter({ site: value === ALL ? undefined : value, building: undefined })}
          >
            <SelectTrigger className="w-56" aria-label={t("defectsPanel.filter.site")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("defectsPanel.filter.allSites")}</SelectItem>
              {sites.map((site) => {
                const id = entityIdAsString(site?._id);

                return id ? (
                  <SelectItem key={id} value={id}>
                    {siteLabel(site)}
                  </SelectItem>
                ) : null;
              })}
            </SelectContent>
          </Select>
        </div>

        {filter.site ? (
          <div className="flex flex-col gap-1">
            <Label>{t("defectsPanel.filter.building")}</Label>
            <Select
              value={filter.building ?? ALL}
              onValueChange={(value) => setFilter({ building: value === ALL ? undefined : value })}
            >
              <SelectTrigger className="w-56" aria-label={t("defectsPanel.filter.building")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("defectsPanel.filter.allBuildings")}</SelectItem>
                {buildings.map((building) => {
                  const id = entityIdAsString(building?._id);

                  return id ? (
                    <SelectItem key={id} value={id}>
                      {building.name || [building.street, building.streetNumber].filter(Boolean).join(" ") || id}
                    </SelectItem>
                  ) : null;
                })}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="flex items-center gap-2 pb-2">
          <Switch id={mineId} checked={filter.mine} onCheckedChange={(mine) => setFilter({ mine })} />
          <Label htmlFor={mineId}>{t("defectsPanel.filter.mine")}</Label>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{t("defectsPanel.loadError", { error })}</AlertDescription>
        </Alert>
      ) : null}

      {truncated ? (
        <Alert>
          <AlertDescription>{t("defectsPanel.truncated")}</AlertDescription>
        </Alert>
      ) : null}

      <DataTable
        columns={columns}
        data={defects}
        loading={loading}
        onRowClick={(defect) => setSelected(defect)}
        emptyMessage={
          <span className="inline-flex items-center gap-2">
            <Wrench className="h-4 w-4" aria-hidden />
            {t("defectsPanel.empty")}
          </span>
        }
      />

      <CreateDefectDialog
        open={creating}
        customer={customer}
        users={users}
        onClose={() => setCreating(false)}
        onCreated={(defect) => {
          // Der neue Mangel gleich im Detail — dort steht seine Nummer und die berechnete Frist.
          setCreating(false);
          setSelected(defect);
          void load();
        }}
      />

      {detail}
    </div>
  );
}
